import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "components/Header";
import { routes } from "constants/routes";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { ISummaryData } from "models/HaulPay";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { QUERY_FUNDING_REQUEST_SUMMARY } from "../../Broker/ManagePay/query";
import FRListByStatusDesktop from "../../modules/FRListByStatusDesktop";
import SearchInput from "../../modules/FRListByStatusDesktop/components/SearchInput";
import StatusSummary from "../../Shared/StatusSummary";
import styles from "./style.module.scss";

interface ManagePayHistoryState {
  debtor?: string;
  debtorName?: string;
  forceUpdate?: boolean;
}

export default function CarrierManagePay() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDesktop } = useDeviceType();
  const location = useLocation();
  const state = location.state as ManagePayHistoryState | undefined;

  const {
    data: FRSummaryData,
    error,
    loading,
    refetch
  } = useQuery(QUERY_FUNDING_REQUEST_SUMMARY, {
    variables: {
      debtor: state?.debtor
    },
    fetchPolicy: "cache-and-network"
  });

  const handleSearch = (value: string) => {
    navigate(`${location.pathname}/all`, { state: { ...state, searchValue: value } });
  };

  useEffect(() => {
    dispatch(handleLoading(loading));

    if (error) showErrorMessage(error.message);
  }, [error, loading]);

  const RenderFundingRequestsComponent = useMemo(() => {
    const Component = ({ summaryData }: { summaryData: ISummaryData }) => {
      return !isDesktop ? (
        <>
          <SearchInput
            initialValue={""}
            onSearch={handleSearch}
            isMobile={true}
            iconClassName="absolute inset-y-0 my-auto text-placeholder left-4"
          />
          <StatusSummary
            summaryData={summaryData}
            debtor={state?.debtor}
            debtorName={state?.debtorName}
          />
        </>
      ) : (
        <FRListByStatusDesktop
          summaryData={summaryData}
          debtor={state?.debtor}
          refetchSummaryData={refetch}
          debtorName={state?.debtorName}
        />
      );
    };

    Component.displayName = "RenderFundingRequestsComponent";
    return Component;
  }, [isDesktop, state?.forceUpdate, state?.debtor, state?.debtorName, refetch]);

  return (
    <MainLayout
      desktopTitle="Manage Pays"
      customHeader={<Header label="Manage Pay" goBackPath={routes.HAULPAY} isHideDesktop />}
      className={styles.managePayContainer}>
      {FRSummaryData && (
        <RenderFundingRequestsComponent summaryData={FRSummaryData.fundingRequestsSummary.data} />
      )}
    </MainLayout>
  );
}
